import { unref, onMounted, onBeforeUnmount } from "vue";
import { onBeforeRouteLeave } from "vue-router";
import { useConfirm } from "@/shared/ui/alert-dialog";

/**
 * Предупреждает пользователя о несохранённых изменениях при уходе со страницы.
 *
 * Вешает две защиты:
 *  - beforeunload — нативное предупреждение браузера (перезагрузка, закрытие
 *    вкладки, переход на внешний URL);
 *  - onBeforeRouteLeave — confirm-диалог в стиле проекта при SPA-навигации.
 *
 * @param {import('vue').Ref<boolean>|import('vue').ComputedRef<boolean>|() => boolean} isDirty
 * @param {object} [options]
 * @param {string} [options.title]
 * @param {string} [options.message]
 * @param {string} [options.confirmButtonText]
 * @param {string} [options.cancelButtonText]
 */
export function useUnsavedChangesGuard(isDirty, options = {}) {
  const confirm = useConfirm();

  const {
    title = "Несохранённые изменения",
    message = "На странице есть несохранённые изменения. Если вы уйдёте, они будут потеряны. Продолжить?",
    confirmButtonText = "Уйти без сохранения",
    cancelButtonText = "Остаться",
  } = options;

  const dirty = () => Boolean(unref(isDirty));

  function onBeforeUnload(e) {
    if (!dirty()) return;
    // Современные браузеры показывают своё стандартное сообщение,
    // но preventDefault + returnValue обязательны для срабатывания.
    e.preventDefault();
    e.returnValue = "";
    return "";
  }

  onMounted(() => {
    window.addEventListener("beforeunload", onBeforeUnload);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("beforeunload", onBeforeUnload);
  });

  onBeforeRouteLeave(async () => {
    if (!dirty()) return true;
    try {
      await confirm(message, title, {
        type: "warning",
        confirmButtonText,
        cancelButtonText,
      });
      return true;
    } catch {
      return false;
    }
  });
}
